import { FETCH_ERROR } from "@boark/shared/consts/fetch-error";
import { IssueCommentCreate } from "@boark/shared/types/issue-comment-create";
import { IssueCommentUpdate } from "@boark/shared/types/issue-comment-update";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createIssueComment,
  deleteIssueComment,
  getIssueComments,
  updateIssueComment,
} from "../services/issue-comment";

export function issueCommentsQueryKey(issueIdentifier: string | number) {
  return ["issue-comments", String(issueIdentifier)] as const;
}

export function useGetIssueComments(issueIdentifier: string | number) {
  return useQuery({
    queryKey: issueCommentsQueryKey(issueIdentifier),
    queryFn: () => getIssueComments(issueIdentifier),
    retry: (_failureCount, error: unknown) =>
      error !== FETCH_ERROR.UNAUTHORIZED,
  });
}

export function useCreateIssueComment(issueIdentifier: string | number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (comment: IssueCommentCreate) => {
      const created = await createIssueComment(issueIdentifier, comment);
      queryClient.setQueryData(
        issueCommentsQueryKey(issueIdentifier),
        (existing) => [created, ...(existing ?? [])],
      );
      return created;
    },
  });
}

export function useUpdateIssueComment(issueIdentifier: string | number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      commentId,
      comment,
    }: {
      commentId: string;
      comment: IssueCommentUpdate;
    }) => {
      const updated = await updateIssueComment(
        issueIdentifier,
        commentId,
        comment,
      );
      queryClient.setQueryData(
        issueCommentsQueryKey(issueIdentifier),
        (existing) =>
          existing?.map((item) =>
            item.id === updated.id ? updated : item,
          ) ?? [updated],
      );
      return updated;
    },
  });
}

export function useDeleteIssueComment(issueIdentifier: string | number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (commentId: string) => {
      await deleteIssueComment(issueIdentifier, commentId);
      queryClient.setQueryData(
        issueCommentsQueryKey(issueIdentifier),
        (existing) => existing?.filter((item) => item.id !== commentId) ?? [],
      );
    },
  });
}
